import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Code, Rocket, Coffee, Zap, Award, CheckCircle, HeartHandshake } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Reveal({ children, delay = 0, direction = 'up' }) {
  const [ref, inView] = useInView(0.1);
  const transforms = { up: 'translateY(28px)', left: 'translateX(-28px)', right: 'translateX(28px)' };
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'none' : transforms[direction],
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>
      {children}
    </div>
  );
}

export default function UeberUns({ darkMode = false }) {
  const { t } = useTranslation();

  const bg          = darkMode ? '#0a0a0f' : '#f8faff';
  const textPrimary = darkMode ? '#ffffff' : '#0f0f1a';
  const textSecondary = darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(15,15,26,0.55)';
  const cardBg      = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)';
  const cardBorder  = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const accent      = '#6366f1';
  const accent2     = '#8b5cf6';
  const accent3     = '#a855f7';

  useEffect(() => {
    document.documentElement.style.background = bg;
    document.body.style.background = bg;
    return () => { document.documentElement.style.background = ''; document.body.style.background = ''; };
  }, [bg]);

  const milestones = [
    { Icon: Coffee, color: accent,    year: t('about.milestone1Year'), title: t('about.milestone1Title'), text: t('about.milestone1Text') },
    { Icon: Rocket, color: accent2,   year: t('about.milestone2Year'), title: t('about.milestone2Title'), text: t('about.milestone2Text') },
    { Icon: Zap,    color: accent3,   year: t('about.milestone3Year'), title: t('about.milestone3Title'), text: t('about.milestone3Text') },
    { Icon: Award,  color: '#ec4899', year: t('about.milestone4Year'), title: t('about.milestone4Title'), text: t('about.milestone4Text') },
  ];

  const values = [
    { Icon: Code,          color: accent,   title: t('about.value1Title'), desc: t('about.value1Desc') },
    { Icon: HeartHandshake,color: accent2,  title: t('about.value2Title'), desc: t('about.value2Desc') },
    { Icon: Rocket,        color: accent3,  title: t('about.value3Title'), desc: t('about.value3Desc') },
  ];

  const allSkills = ['skill1','skill2','skill3','skill4','skill5','skill6','skill7','skill8'];

  return (
    <div style={{ background: bg, color: textPrimary, minHeight: '100vh' }}>

      {/* ── HERO ── */}
      <section style={{
        position: 'relative', minHeight: '50vh', display: 'flex', alignItems: 'center',
        paddingTop: 'clamp(100px, 14vh, 140px)', paddingBottom: 64, overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: darkMode
          ? 'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(99,102,241,0.16) 0%, transparent 65%)'
          : 'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(99,102,241,0.08) 0%, transparent 65%)' }} />

        <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative', width: '100%' }}>
          <Reveal>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20,
              padding: '5px 16px', borderRadius: 999, fontSize: 12, color: accent2,
              background: darkMode ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.08)',
              border: `1px solid ${darkMode ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.22)'}`,
            }}>
              <Rocket size={13} />
              {t('about.founded')}
            </div>
            <h1 style={{ fontSize: 'clamp(30px, 5vw, 64px)', fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.03em', marginBottom: 16 }}>
              {t('about.heroTitle')}{' '}
              <span style={{ background: `linear-gradient(135deg, ${accent}, ${accent2}, ${accent3})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                DerrDigital
              </span>
            </h1>
            <p style={{ fontSize: 'clamp(14px, 1.5vw, 18px)', color: textSecondary, lineHeight: 1.7, maxWidth: 560, margin: '0 auto' }}>
              {t('about.heroSubtitle')}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── TEAM ── */}
      <section style={{ padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: accent2, textTransform: 'uppercase', marginBottom: 10 }}>Entwickler</div>
              <h2 style={{ fontSize: 'clamp(22px, 3vw, 44px)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                {t('about.teamTitle')}
              </h2>
              <p style={{ color: textSecondary, fontSize: 14, marginTop: 10, maxWidth: 480, margin: '10px auto 0' }}>
                {t('about.teamSubtitle')}
              </p>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24, maxWidth: 560, margin: '0 auto' }}>
            {[
              { name: 'Dustin Derr', role: t('about.person1Role'), desc: t('about.person1Desc'), Icon: Code, color: accent, skills: allSkills, delay: 0 },
            ].map(p => (
              <Reveal key={p.name} delay={p.delay}>
                <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 18, overflow: 'hidden', transition: 'border-color 0.3s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = p.color + '40'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = cardBorder}
                >
                  {/* Card header */}
                  <div style={{ padding: '32px 28px 24px', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${p.color}, transparent)` }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                      <div style={{ width: 52, height: 52, borderRadius: 14, background: p.color + '18', border: `1px solid ${p.color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: p.color, flexShrink: 0 }}>
                        <p.Icon size={24} strokeWidth={1.5} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 18, color: textPrimary }}>{p.name}</div>
                        <div style={{ fontSize: 13, color: p.color, fontWeight: 600, marginTop: 2 }}>{p.role}</div>
                      </div>
                    </div>
                    <p style={{ color: textSecondary, fontSize: 14, lineHeight: 1.65 }}>{p.desc}</p>
                  </div>
                  {/* Skills */}
                  <div style={{ padding: '16px 28px 24px', borderTop: `1px solid ${cardBorder}` }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                      {p.skills.map(k => (
                        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <CheckCircle size={13} color={p.color} style={{ flexShrink: 0 }} />
                          <span style={{ fontSize: 13, color: textSecondary }}>{t(`about.${k}`)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── MILESTONES ── */}
      <section style={{ padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: darkMode ? 'rgba(99,102,241,0.03)' : 'rgba(99,102,241,0.02)', borderTop: `1px solid ${cardBorder}`, borderBottom: `1px solid ${cardBorder}` }} />
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: accent2, textTransform: 'uppercase', marginBottom: 10 }}>Geschichte</div>
              <h2 style={{ fontSize: 'clamp(22px, 3vw, 44px)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                {t('about.historyTitle')}
              </h2>
            </div>
          </Reveal>

          <div style={{ position: 'relative' }}>
            {/* vertical line */}
            <div style={{ position: 'absolute', left: 19, top: 0, bottom: 0, width: 1, background: `linear-gradient(to bottom, ${accent}60, ${accent3}60)` }} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {milestones.map((m, i) => (
                <Reveal key={i} delay={i * 0.08} direction="left">
                  <div style={{ display: 'flex', gap: 24, paddingBottom: i < milestones.length - 1 ? 28 : 0 }}>
                    {/* dot */}
                    <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: '50%', background: m.color + '18', border: `1.5px solid ${m.color}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: m.color, position: 'relative', zIndex: 1 }}>
                      <m.Icon size={17} strokeWidth={1.8} />
                    </div>
                    <div style={{ flex: 1, background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, padding: '16px 20px', marginTop: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 800, fontSize: 15, color: textPrimary }}>{m.title}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: m.color, background: m.color + '15', padding: '2px 8px', borderRadius: 999, letterSpacing: '0.04em' }}>{m.year}</span>
                      </div>
                      <p style={{ color: textSecondary, fontSize: 13, lineHeight: 1.65, margin: 0 }}>{m.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section style={{ padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: accent2, textTransform: 'uppercase', marginBottom: 10 }}>Werte</div>
              <h2 style={{ fontSize: 'clamp(22px, 3vw, 44px)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                {t('about.valuesTitle')}
              </h2>
              <p style={{ color: textSecondary, fontSize: 14, marginTop: 10, maxWidth: 440, margin: '10px auto 0' }}>
                {t('about.valuesSubtitle')}
              </p>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
            {values.map((v, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 16, padding: '28px 24px', textAlign: 'center', position: 'relative', overflow: 'hidden', transition: 'border-color 0.3s, transform 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = v.color + '45'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = cardBorder; e.currentTarget.style.transform = 'none'; }}
                >
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${v.color}, transparent)` }} />
                  <div style={{ width: 48, height: 48, borderRadius: 13, background: v.color + '18', border: `1px solid ${v.color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: v.color }}>
                    <v.Icon size={22} strokeWidth={1.5} />
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: textPrimary, marginBottom: 8 }}>{v.title}</div>
                  <p style={{ color: textSecondary, fontSize: 13, lineHeight: 1.65, margin: 0 }}>{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: 'clamp(40px, 6vh, 64px) 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative' }}>
          <Reveal>
            <h2 style={{ fontSize: 'clamp(17px, 2vw, 26px)', fontWeight: 900, color: '#fff', lineHeight: 1.1, marginBottom: 10 }}>
              {t('about.ctaTitle')}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.65, marginBottom: 28, maxWidth: 480, margin: '0 auto 28px' }}>
              {t('about.ctaDesc')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/kontakt" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px', borderRadius: 12,
                background: '#fff', color: accent, fontWeight: 700, fontSize: 14, textDecoration: 'none',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)', transition: 'transform 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                {t('about.ctaDiscuss')}
              </Link>
              <Link to="/referenzen" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px', borderRadius: 12,
                border: '1.5px solid rgba(255,255,255,0.4)', color: '#fff', fontWeight: 600, fontSize: 14, textDecoration: 'none',
                background: 'rgba(255,255,255,0.1)', transition: 'background 0.2s, border-color 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.7)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
              >
                {t('about.ctaReferences')}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
